import React, { useState, useMemo } from 'react';
import { Upload, CheckCircle, Clock, ChevronRight, Eye, FileText, Building2, Filter, Search } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { STATUS_CONFIG, formatINR } from '@/data/mockData';

const SALES_STATUS_CONFIG = {
  PENDING_REVIEW: { label: 'Pending Review', className: 'bg-amber-100 text-amber-700' },
  UNDER_REVIEW: { label: 'Under Review', className: 'bg-blue-100 text-blue-700' },
  APPROVED: { label: 'Approved', className: 'bg-green-100 text-green-700' },
  REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
  VERIFIED: { label: 'Verified', className: 'bg-green-100 text-green-700' },
};

export default function InvoiceSummaryScreen() {
  const { salesInvoices, navigate } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Calculate totals
  const totalSalesValue = useMemo(() => 
    salesInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
    [salesInvoices]
  );

  const approvedValue = useMemo(() => 
    salesInvoices.filter(inv => ['APPROVED', 'VERIFIED'].includes(inv.status))
      .reduce((sum, inv) => sum + (inv.amount || 0), 0),
    [salesInvoices]
  );

  const pendingValue = useMemo(() => 
    salesInvoices.filter(inv => ['PENDING_REVIEW', 'UNDER_REVIEW'].includes(inv.status))
      .reduce((sum, inv) => sum + (inv.amount || 0), 0),
    [salesInvoices]
  );

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return salesInvoices.filter(inv => {
      const matchesSearch = searchQuery === '' || 
        inv.invoiceNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.hospitalName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [salesInvoices, searchQuery, statusFilter]);

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="bg-[#2563EB] px-4 pt-5 pb-14">
        <h1 className="text-white font-black text-xl mb-4">Sales Invoices</h1>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/15 rounded-xl p-3">
            <div className="text-white/70 text-[10px] font-bold uppercase tracking-wider">Total Sales</div>
            <div className="rupee-value text-white font-black text-sm mt-0.5">₹{formatINR(totalSalesValue)}</div>
          </div>
          <div className="bg-white/15 rounded-xl p-3">
            <div className="text-white/70 text-[10px] font-bold uppercase tracking-wider">Approved</div>
            <div className="rupee-value text-green-300 font-black text-sm mt-0.5">₹{formatINR(approvedValue)}</div>
          </div>
          <div className="bg-white/15 rounded-xl p-3">
            <div className="text-white/70 text-[10px] font-bold uppercase tracking-wider">Pending</div>
            <div className="rupee-value text-amber-300 font-black text-sm mt-0.5">₹{formatINR(pendingValue)}</div>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-6 flex flex-col gap-4">

        {/* Upload New Invoice CTA */}
        <button
          data-testid="upload-sales-invoice-cta"
          onClick={() => navigate('SALES_INVOICE_UPLOAD')}
          className="w-full bg-[#2563EB] rounded-2xl p-5 flex items-center justify-between shadow-lg tap-scale slide-up"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Upload size={24} color="white" />
            </div>
            <div className="text-left">
              <div className="text-white font-black text-lg leading-tight">Upload Sales Invoice</div>
              <div className="text-white/80 text-xs font-medium mt-0.5">
                Record your hospital sales
              </div>
            </div>
          </div>
          <ChevronRight size={22} color="rgba(255,255,255,0.8)" />
        </button>

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 slide-up-d1">
          <div className="flex gap-2 mb-3">
            <div className="flex-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
              <Search size={16} color="#94A3B8" />
              <input
                data-testid="sales-invoice-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search invoice or hospital..."
                className="flex-1 text-sm outline-none bg-transparent text-slate-700 placeholder-slate-400"
              />
            </div>
          </div>
          
          {/* Status Filter Pills */}
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'All' },
              { value: 'UNDER_REVIEW', label: 'Under Review' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'VERIFIED', label: 'Verified' },
            ].map(({ value, label }) => (
              <button
                key={value}
                data-testid={`filter-${value}`}
                onClick={() => setStatusFilter(value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold tap-scale transition-all ${
                  statusFilter === value 
                    ? 'bg-[#2563EB] text-white' 
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice Count */}
        <div className="flex justify-between items-center">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            {filteredInvoices.length} Invoice{filteredInvoices.length !== 1 ? 's' : ''}
          </span>
          <span className="text-xs font-bold text-[#1E2744]">
            ₹{formatINR(filteredInvoices.reduce((s, i) => s + (i.amount || 0), 0))}
          </span>
        </div>

        {/* Invoices List */}
        <div className="slide-up-d2">
          {filteredInvoices.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100 shadow-sm">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText size={32} color="#2563EB" />
              </div>
              <div className="font-bold text-slate-700 text-base mb-1">No Sales Invoices Yet</div>
              <div className="text-sm text-slate-400 mb-4">Upload your first hospital sales invoice</div>
              <button
                data-testid="empty-state-upload-btn"
                onClick={() => navigate('SALES_INVOICE_UPLOAD')}
                className="bg-[#2563EB] text-white px-6 py-3 rounded-xl font-bold text-sm tap-scale"
              >
                Upload Invoice
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredInvoices.map((inv, index) => {
                const statusCfg = SALES_STATUS_CONFIG[inv.status] || SALES_STATUS_CONFIG.UNDER_REVIEW;
                const isApproved = ['APPROVED', 'VERIFIED'].includes(inv.status);
                
                return (
                  <div
                    key={inv.id || index}
                    data-testid={`sales-invoice-${inv.id || index}`}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isApproved ? 'bg-green-100' : 'bg-blue-50'}`}>
                          {isApproved ? (
                            <CheckCircle size={20} color="#22C55E" />
                          ) : (
                            <FileText size={20} color="#2563EB" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-[#1E2744] text-sm">{inv.invoiceNo}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{inv.date}</div>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-lg ${statusCfg.className}`}>
                        {statusCfg.label}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-end">
                      <div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-0.5">
                          <Building2 size={12} />
                          Hospital
                        </div>
                        <div className="text-sm font-semibold text-slate-700">{inv.hospitalName}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-400 mb-0.5">Sale Value</div>
                        <div className="rupee-value font-black text-[#1E2744] text-lg">₹{formatINR(inv.amount)}</div>
                      </div>
                    </div>

                    {/* Line Items Preview */}
                    {inv.lineItems && inv.lineItems.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-2">
                          Products ({inv.lineItems.length})
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {inv.lineItems.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-1 rounded-lg">
                              {item.productName}
                            </span>
                          ))}
                          {inv.lineItems.length > 3 && (
                            <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-1 rounded-lg">
                              +{inv.lineItems.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Summary Card */}
        {filteredInvoices.length > 0 && (
          <div className="bg-gradient-to-r from-[#1E2744] to-[#2563EB] rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <CheckCircle size={20} color="white" />
            </div>
            <div>
              <div className="font-bold text-white text-sm">
                ₹{formatINR(approvedValue)} Approved
              </div>
              <div className="text-white/70 text-xs mt-0.5">
                {salesInvoices.filter(i => ['APPROVED', 'VERIFIED'].includes(i.status)).length} invoices verified
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
